import { Card, CardBody, CardHeader, Col, Container, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Form, FormGroup, Input, Label, Nav, NavItem, NavLink, Row, TabContent, TabPane } from 'reactstrap'
import { getClients, getProperties, getPropertyUtilities, getPropertyUtilityMeters } from '../../Services/PortfolioService';
import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Breadcrumbs from '../../CommonElements/Breadcrumbs';
import { Href } from '../../Utils/Constants';
import { MoreVertical, Eye, Pencil, ChartLine } from 'lucide-react';
import { Btn, H4 } from '../../AbstractElements';
import SelectDropdown from '../../CustomComponent/Form/SelectDropdown';
import { useNavigate } from 'react-router-dom';
import { ChartColumnIncreasing } from 'lucide-react';
import { getTrackerChart, getAvailableYears, getTrackerTable } from '../../Services/TrackerService';
import EnergyColumnChart from '../../CustomComponent/Charts/EnergyColumnChart';
import { sortByKey } from '../../Utils';
import EnergyLineChart from '../../CustomComponent/Charts/EnergyLineChart';
import { getDataIntegrityReport } from '../../Services/TrackerService';
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Table from '../../CustomComponent/Tables/Table';
import { TableColumn } from 'react-data-table-component';

export const justifyTabNav = [{ id: 'chart', nav: 'Energy Charts' }, { id: 'energy-report', nav: 'Energy Report' }, { id: 'data-report', nav: 'Data Report' }, { id: 'add', nav: 'Add Data' }];
type billParams = {
    company_id: string,
    property_id?: string,
    utility_id?: string,
    year?: string
}

type trackerParams = {
    client_id: string,
    property_id?: string,
    utility_id?: string,
    site_id?: string,
    meters?: string[],
    checkType: string,
    typeMode: string,
    year?: string,
    month?: string,
    day?: string,
    date?: string,
    start_date?: string,
    end_date?: string,
    with_previous_year?: boolean,
    with_previous_month?: boolean,
    with_previous_day?: boolean
}

interface DayStatus {
    day: number;
    status: "available" | "missing";
}

interface MonthStatus {
    month: number;
    days: DayStatus[];
}

interface YearStatus {
    year: number;
    months: MonthStatus[];
}

export interface BillTableRow {
    property?: { name?: string };
    bill_date: string;
    bill_type: string;
    start_date: string;
    end_date: string;
    utility?: { name?: string; si_unit?: string };
    supplier?: { name?: string };
    total_consumption: number | string;
    cost: number | string;
    currency?: { major_symbol?: string };
}

const reportSchema = yup.object({
    client_id: yup.string().required('Client is required'),
    property_id: yup.string().required("Property is required"),
    utility_id: yup.string().required("Utility is required"),
    site_id: yup.string().required("Meter is required"),
    year: yup.number().nullable(),
    month: yup
        .number()
        .nullable()
        .when("year", {
            is: (val: number | null) => !!val,
            then: (schema) => schema.nullable(),
            otherwise: (schema) => schema.nullable()
        })
});

export default function EnergyTracker() {
    const navigate = useNavigate();
    const [clients, setClients] = useState<any>([]);
    const [selectedClient, setSelectedClient] = useState('');
    const [properties, setProperties] = useState<any>([]);
    const [utilities, setUtilities] = useState<any>([]);
    const [selectedProperty, setSelectedProperty] = useState('');
    const [selectedUtility, setSelectedUtility] = useState('');
    const [utility, setUtility] = useState('');
    const [trackerData, setTrackerData] = useState<any>(null);
    const [meters, setMeters] = useState<any>([]);
    const [selectedMeter, setSelectedMeter] = useState('');
    const [basicTab, setBasicTab] = useState('chart');
    const [activeChart, setActiveChart] = useState('bar');
    const [activeMode, setActiveMode] = useState('Single');
    const [timeMode, setTimeMode] = useState('year');
    const [selectedYear, setSelectedYear] = useState<number>();
    const [withPreviousYear, setWithPreviousYear] = useState<boolean>(false);
    const [selectedMonth, setSelectedMonth] = useState<number>();
    const [withPreviousMonth, setWithPreviousMonth] = useState<boolean>(false);
    const [selectedDay, setSelectedDay] = useState<number>();
    const [withPreviousDay, setWithPreviousDay] = useState<boolean>(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [availableYears, setAvailableYears] = useState<number[]>([]);
    const [dataIntegrityReportData, setDataIntegrityReportData] = useState<YearStatus[]>(() => []);
    const [energyTableData, setEnergyTableData] = useState([]);

    useEffect(() => {
        if (timeMode === 'Year') {
            setWithPreviousMonth(false);
        }
    }, [timeMode]);

    const {
        handleSubmit,
        control,
        formState: { errors },
        watch,
        reset
    } = useForm({
        resolver: yupResolver(reportSchema),
        defaultValues: {
            client_id: "",
            property_id: "",
            utility_id: "",
            site_id: "",
            year: null,
            month: null
        }
    });

    useEffect(() => {
        fetchClients();
        const storedClient = localStorage.getItem('selectedClient');
        if (storedClient) {
            setSelectedClient(storedClient);
        }
    }, []);

    useEffect(() => {
        if (selectedClient) {
            reset((prev) => ({
                ...prev,
                client_id: selectedClient
            }));
        }
    }, [selectedClient, reset]);

    useEffect(() => {
        if (!selectedClient) return;
        setProperties([]);
        setSelectedProperty('');
        setUtilities([]);
        setSelectedUtility('');
        setMeters([]);
        setSelectedMeter('');
        fetchProperties(selectedClient);
        localStorage.setItem('selectedClient', selectedClient);

        // Fetch available years for this client
        const fetchYears = async () => {
            try {
                const years: number[] = await getAvailableYears(selectedClient);
                setAvailableYears(Array.isArray(years) ? years : []);
            } catch (error: any) {
                setAvailableYears([]);
                toast.error(error?.message || 'Failed to fetch available years');
            }
        };

        fetchYears();
    }, [selectedClient]);

    const reportYear = watch('year');
    const reportMonth = watch('month');

    const setAvailableMonths = (year: any) => {
        const months = Array.from({ length: 12 }, (_, i) => i + 1);

        return months.map(month => ({
            value: month,
            label: new Date(0, month - 1).toLocaleString('default', { month: 'long' })
        }));
    };

    const availableMonths = setAvailableMonths(reportYear);

    useEffect(() => {
        if (!selectedProperty) return;
        fetchPropertyUtilities();
    }, [selectedProperty]);

    useEffect(() => {
        if (!selectedUtility) return;
        fetchUtilityMeters();
    }, [selectedUtility]);

    const fetchClients = async () => {
        try {
            const data: any[] = await getClients();
            setClients(sortByKey(data, 'name'));
        } catch (error: any) {
            toast.error(error?.message || 'Failed to fetch clients');
        }
    };

    const fetchProperties = async (selectedClient: string) => {
        if (selectedClient === '') return;
        try {
            const { properties }: { properties: any[] } = await getProperties(selectedClient);
            setProperties(sortByKey(properties, 'name'));
        } catch (error: any) {
            toast.error(error?.message || 'Failed to fetch Properties');
        }
    };

    const fetchPropertyUtilities = async () => {
        if (!selectedProperty) return;
        try {
            const data: any[] = await getPropertyUtilities(selectedClient, selectedProperty);
            setUtilities(sortByKey(data, 'name'));
        } catch (error: any) {
            toast.error(error?.message || 'Failed to fetch Properties');
        }
    };

    const fetchUtilityMeters = async () => {
        try {
            const data: any[] = await getPropertyUtilityMeters(selectedClient, selectedProperty, selectedUtility);
            setMeters(sortByKey(data, 'name'));
        } catch (error: any) {
            toast.error(error?.message || 'Failed to fetch Properties');
        }
    };

    // Prepare options for DropItem
    const clientOptions = clients.map((client: { client_id: string; name: string }) => ({
        value: client.client_id,
        label: client.name
    }));

    const propertyOptions = properties.map((property: { property_id: string; name: string }) => ({
        value: property.property_id,
        label: property.name
    }));

    const utilityOptions = utilities.map((utility: { utility_id: string; name: string }) => ({
        value: utility.utility_id,
        label: utility.name
    }));

    const meterOptions = meters.map((meter: { site_id: string; name: string }) => ({
        value: meter.site_id,
        label: meter.name
    }));

    const handleClientChange = (selectedValue: any) => {
        setSelectedClient(selectedValue as string);
        localStorage.setItem('selectedClient', selectedValue as string);
    };

    const handlePropertyChange = (selectedValue: any) => {
        setSelectedProperty(selectedValue as string);
        setUtilities([]);
        setSelectedUtility('');
        setMeters([]);
        setSelectedMeter('');
    };

    const handleUtilityChange = (selectedValue: any) => {
        setSelectedUtility(selectedValue as string);
        setMeters([]);
        setSelectedMeter('');
    };

    const ActionDropdown = ({ row }: { row: any }) => {
        const [dropdownOpen, setDropdownOpen] = useState(false);
        const toggle = () => setDropdownOpen(prevState => !prevState);

        return (
            <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down">
                <DropdownToggle color="transparent" className="p-0 border-0 shadow-none">
                    <MoreVertical size={18} />
                </DropdownToggle>
                <DropdownMenu end>
                    <DropdownItem onClick={() => navigate(`${process.env.PUBLIC_URL}/clients/${row.bill_id}`)}>
                        <Eye size={16} color="green" className="me-2" />
                        View
                    </DropdownItem>
                    <DropdownItem onClick={() => console.log('Edit', row)}>
                        <Pencil size={16} color="orange" className="me-2" />
                        Edit
                    </DropdownItem>
                </DropdownMenu>
            </Dropdown>
        );
    };

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    const currentDay = currentDate.getDate();

    const months = Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
        const isDisabled = selectedYear === currentYear && month > currentMonth;
        const isUsedUp = selectedYear === currentYear && month > currentMonth;

        return (
            <Col md="3" className={`border p-1 ${selectedMonth === month ? "bg-primary" : ""} ${!isDisabled ? 'cursor-pointer' : ''} ${selectedYear ? "" : "disabled"}`}
                onClick={() => {
                    if (!isDisabled && selectedYear) {
                        setSelectedMonth(month);
                        setSelectedDay(undefined);
                    }
                }}
                style={{
                    fontSize: '0.8rem',
                    backgroundColor: isUsedUp ? '#ccc' : ''
                }}
                key={month}
            >
                {new Date(0, month - 1).toLocaleString('default', { month: 'short' })}
            </Col>
        );
    });

    const daysInSelectedMonth = selectedYear && selectedMonth ? new Date(selectedYear, selectedMonth, 0).getDate() : 31;
    const dayCells = Array.from({ length: daysInSelectedMonth }, (_, i) => i + 1).map((day) => {
        const isFutureDay = selectedYear === currentYear && selectedMonth === currentMonth && day >= currentDay;
        const isDisabled = !selectedYear || !selectedMonth || isFutureDay;

        return (
            <div
                key={day}
                className={`border p-1 text-center ${selectedDay === day ? "bg-primary text-white" : ""} ${!isDisabled ? 'cursor-pointer' : 'disabled'}`}
                onClick={() => !isDisabled && setSelectedDay(day)}
                style={{
                    fontSize: '0.75rem',
                    backgroundColor: isDisabled ? '#f8f9fa' : undefined,
                    color: isDisabled && selectedDay !== day ? '#a0a0a0' : undefined,
                }}
            >
                {day}
            </div>
        );
    });

    const days = (
        <div
            className="d-grid"
            style={{
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            }}
        >
            {dayCells}
        </div>
    );

    const getChartData = async (e: FormEvent) => {
        e.preventDefault()

        try {
            const params: trackerParams = {
                site_id: selectedMeter,
                client_id: selectedClient,
                property_id: selectedProperty,
                utility_id: selectedUtility,
                year: selectedYear?.toString(),
                month: selectedMonth?.toString(),
                day: timeMode === 'Daily' ? selectedDay?.toString() : undefined,
                date: selectedYear && selectedMonth && (timeMode === 'Daily' && selectedDay)
                    ? `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`
                    : selectedYear && selectedMonth
                        ? `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`
                        : selectedYear
                            ? `${selectedYear}`
                            : undefined,
                typeMode: activeMode,
                checkType: timeMode,
                start_date: startDate,
                end_date: endDate,
                with_previous_year: withPreviousYear,
                with_previous_month: withPreviousMonth,
                with_previous_day: withPreviousDay
            };

            const { data, previous, month, day, year } = await getTrackerChart(params);

            let formattedData = {}
            switch (timeMode) {
                case 'Multiyear':
                    formattedData = {
                        categories: data.map((item: { year: number }) => item.year.toString()),
                        series: [{
                            name: 'Energy Consumption',
                            data: data.map((item: { energy_value: number }) => item.energy_value)
                        }],
                        si_unit: utility === 'Electricity' ? 'kWh' : utility === 'Gas' ? 'Es' : utility === 'Water' ? 'CuM2' : '',
                    };
                    break;
                case 'Year':
                    const previousYearData = previous?.data;
                    formattedData = {
                        categories: data.map((item: { month: string }) => item.month),
                        series: [
                            ...(previousYearData && selectedYear ? [{
                                name: (selectedYear - 1).toString(),
                                data: previousYearData.map((item: { energy_value: number }) => item.energy_value)
                            }] : []),
                            {
                                name: selectedYear,
                                data: data.map((item: { energy_value: number }) => item.energy_value)
                            }
                        ],
                        si_unit: utility === 'Electricity' ? 'kWh' : utility === 'Gas' ? 'Es' : utility === 'Water' ? 'CuM2' : '',
                    };
                    break;
                case 'Monthly':
                    const previousMonthData = previous?.data;

                    let previousLabel = `${previous?.month ?? ''} ${previous?.year ?? ''}`.trim();

                    let currentLabel = `${month ?? ''} ${year ?? ''}`.trim();

                    formattedData = {
                        categories: data.map((item: { day: number }) => item.day),
                        series: [
                            ...(previousMonthData && selectedMonth ? [{
                                // name: previous?.month,
                                name: previousLabel,
                                data: previousMonthData.map((item: { energy_value: number }) => item.energy_value)
                            }] : []),
                            {
                                // name: month,
                                name: currentLabel,
                                data: data.map((item: { energy_value: number }) => item.energy_value)
                            }
                        ],
                        si_unit: utility === 'Electricity' ? 'kWh' : utility === 'Gas' ? 'Es' : utility === 'Water' ? 'CuM2' : '',
                    };
                    break;
                case 'Daily':
                    const previousDayData = previous?.data;
                    formattedData = {
                        categories: data.map((item: { time: number }) => item.time),
                        series: [
                            ...(previousDayData && selectedDay ? [{
                                name: `${previous.month} ${previous.day}`,
                                data: previousDayData.map((item: { energy_value: number }) => item.energy_value)
                            }] : []),
                            {
                                name: `${month} ${day}`,
                                data: data.map((item: { energy_value: number }) => item.energy_value)
                            }
                        ],
                        si_unit: utility === 'Electricity' ? 'kWh' : utility === 'Gas' ? 'Es' : utility === 'Water' ? 'CuM2' : '',
                    };
                    break;
                case 'From/To':
                    formattedData = {
                        categories: data.map((item: { day: string }) => item.day),
                        series: [{
                            name: 'Energy Consumption',
                            data: data.map((item: { energy_value: number }) => item.energy_value)
                        }],
                        si_unit: utility === 'Electricity' ? 'kWh' : utility === 'Gas' ? 'Es' : utility === 'Water' ? 'CuM2' : '',
                    };
                    break;
                default:
                    console.log('Unknown time mode');
            }

            setTrackerData(formattedData)
        } catch (error: any) {
            toast.error(error?.message || 'Failed to fetch data');
        }
    };

    const getTableData = async (e: FormEvent) => {    
        e.preventDefault()

        try {
            const params: trackerParams = {
                site_id: selectedMeter,
                client_id: selectedClient,
                property_id: selectedProperty,
                utility_id: selectedUtility,
                year: selectedYear?.toString(),
                month: selectedMonth?.toString(),
                day: timeMode === 'Daily' ? selectedDay?.toString() : undefined,
                date: selectedYear && selectedMonth && (timeMode === 'Daily' && selectedDay)
                    ? `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`
                    : selectedYear && selectedMonth
                        ? `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`
                        : selectedYear
                            ? `${selectedYear}`
                            : undefined,
                typeMode: activeMode,
                checkType: timeMode,
                start_date: startDate,
                end_date: endDate,
            };

            // const { data, previous, month, day, year } = await getTrackerTable(params);
            const {total_energy, rows} = await getTrackerTable(params);
            setEnergyTableData(rows)

        } catch (error: any) {
            toast.error(error?.message || 'Failed to fetch data');
        }
    }

    const onSubmitReport = async (formData: any) => {
        try {
            const data = await getDataIntegrityReport(formData);
            setDataIntegrityReportData(data);
        } catch (error: any) {
            toast.error(error?.message || "Failed to fetch energy data Integrity report");
        }
    };

    const filteredIntegrityData = dataIntegrityReportData
        .filter((yearItem) =>
            reportYear ? yearItem.year == reportYear : true
        )
        .map((yearItem) => ({
            ...yearItem,
            months: yearItem.months.filter((monthItem) =>
                reportMonth ? monthItem.month == reportMonth : true
            )
        }))
        .filter((yearItem) => yearItem.months.length > 0);

    const EnergyDataIntegrityReport = ({ data }: { data: YearStatus[] }) => {
        return (
            <div>
                {data.map((yearItem) => (
                    <div key={yearItem.year} className="mb-4">
                        <h3>{yearItem.year}</h3>

                        {yearItem.months.map((monthItem) => (
                            <div key={monthItem.month} className="mb-3">
                                <h5>
                                    {new Date(0, monthItem.month - 1).toLocaleString("default", {
                                        month: "long",
                                    })}
                                </h5>

                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(7, 1fr)",
                                        gap: "5px",
                                    }}
                                >
                                    {monthItem.days.map((dayItem) => (
                                        <div
                                            key={dayItem.day}
                                            style={{
                                                padding: "6px",
                                                textAlign: "center",
                                                borderRadius: "4px",
                                                fontSize: "0.8rem",
                                                backgroundColor:
                                                    dayItem.status === "available"
                                                        ? "#28a745" // green
                                                        : "#dc3545", // red
                                                color: "white",
                                            }}
                                        >
                                            {dayItem.day}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    };

    const energyTableColumns: TableColumn<any>[] = [
        {
            name: "Period",
            selector: (row) => row.period || '',
            sortable: true,
            center: false,
            width: '150px',
        },
        {
            name: "Energy Value",
            selector: (row) => row.energy_value !== undefined ? row.energy_value : '',
            sortable: false,
            center: true,
            width: '140px',
        },
        {
            name: "Meter Name",
            selector: (row) => row.meter_name || '',
            sortable: false,
            center: false,
            width: '170px',
        },
        {
            name: "Location",
            selector: (row) => row.location || '',
            sortable: false,
            center: false,
            width: '200px',
        },
    ];

    return (
        <>
            <Row>
                <Col md="8" className=''>
                    <Breadcrumbs pageTitle='Energy Tracker' />
                </Col>
                <Col md="4" className="d-flex justify-content-end mt-3">
                    <SelectDropdown
                        title="Clients"
                        options={clientOptions}
                        value={selectedClient}
                        onOptionSelected={(val) =>
                            handleClientChange(val)
                        }
                    />
                </Col>
            </Row>

            <Container className='default-dashboard' fluid>
                <Row style={{ textAlign: 'center' }}>
                    <Col md={basicTab === 'chart' || basicTab === 'energy-report' ? 9 : 12}>
                        <Card>
                            <CardBody>
                                <CardHeader className='d-flex justify-content-between align-items-center flex-wrap gap-2 pb-2 p-0'>
                                    <Nav className='nav-primary' pills>
                                        {justifyTabNav.map((item) => (
                                            <NavItem key={item.id}>
                                                <NavLink href={Href} className={`${basicTab === item.id ? 'active' : ''}`} onClick={() => setBasicTab(item.id)}>{item.nav}</NavLink>
                                            </NavItem>
                                        ))}
                                    </Nav>
                                </CardHeader>
                                <CardBody className='px-0 pb-0'>
                                    <TabContent activeTab={basicTab}>
                                        <TabPane tabId='chart'>
                                            <Row className="justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                                                <Col lg="12" className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                                                    {trackerData && activeChart === 'bar' && (
                                                        <EnergyColumnChart title='Chart' data={trackerData} height={window.innerHeight * 0.64} width={window.innerWidth * 0.54} size={12} />
                                                    )}
                                                    {trackerData && activeChart === 'line' && (
                                                        <EnergyLineChart title='Chart' data={trackerData} height={window.innerHeight * 0.64} width={window.innerWidth * 0.54} size={12} />
                                                    )}
                                                </Col>
                                            </Row>
                                        </TabPane>
                                        <TabPane tabId='energy-report'>
                                            <Row className='p-3'>
                                                <Table title='' tableColumns={energyTableColumns} data={energyTableData}  />
                                            </Row>
                                        </TabPane>
                                        <TabPane tabId='data-report'>
                                            <Row className="p-3">
                                                <Col md="12" className="mb-3">
                                                    <Row className="text-start align-items-end">
                                                        <Col md="2">
                                                            <FormGroup>
                                                                <Controller
                                                                    name="property_id"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <SelectDropdown
                                                                            title="Properties"
                                                                            options={propertyOptions}
                                                                            value={field.value}
                                                                            onOptionSelected={(val) => {
                                                                                field.onChange(val);
                                                                                handlePropertyChange(val);
                                                                            }}
                                                                        />
                                                                    )}
                                                                />
                                                                {errors.property_id && (
                                                                    <small className="text-danger">
                                                                        {errors.property_id.message}
                                                                    </small>
                                                                )}
                                                            </FormGroup>
                                                        </Col>

                                                        <Col md="2">
                                                            <FormGroup>
                                                                <Controller
                                                                    name="utility_id"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <>
                                                                            <SelectDropdown
                                                                                title="Utility"
                                                                                options={utilityOptions}
                                                                                value={field.value}
                                                                                onOptionSelected={(val) => {
                                                                                    field.onChange(val);
                                                                                    handleUtilityChange(val); // optional if you have cascading logic
                                                                                }}
                                                                            />
                                                                            {errors.utility_id && (
                                                                                <small className="text-danger">
                                                                                    {errors.utility_id.message}
                                                                                </small>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                />
                                                            </FormGroup>
                                                        </Col>

                                                        <Col md="2">
                                                            <FormGroup>
                                                                <Controller
                                                                    name="site_id"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <>
                                                                            <SelectDropdown
                                                                                title="Meter"
                                                                                options={meterOptions}
                                                                                value={field.value}
                                                                                onOptionSelected={(val) => {
                                                                                    field.onChange(val);
                                                                                }}
                                                                            />
                                                                            {errors.site_id && (
                                                                                <small className="text-danger">
                                                                                    {errors.site_id.message}
                                                                                </small>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                />
                                                            </FormGroup>
                                                        </Col>

                                                        <Col md="2">
                                                            <Controller
                                                                name="year"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <>
                                                                        <SelectDropdown
                                                                            title="Year"
                                                                            options={availableYears.map((year) => ({ value: year, label: year.toString() }))}
                                                                            value={field.value ?? undefined}
                                                                            onOptionSelected={(val) => field.onChange(val)}
                                                                        />
                                                                        {errors.year && (
                                                                            <small className="text-danger">
                                                                                {errors.year.message}
                                                                            </small>
                                                                        )}
                                                                    </>
                                                                )}
                                                            />
                                                        </Col>

                                                        <Col md="2">
                                                            <Controller
                                                                name="month"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <>
                                                                        <SelectDropdown
                                                                            title="Month"
                                                                            options={availableMonths}
                                                                            value={field.value ?? undefined}
                                                                            onOptionSelected={(val) => field.onChange(val)}
                                                                        />
                                                                        {errors.month && (
                                                                            <small className="text-danger">
                                                                                {errors.month.message}
                                                                            </small>
                                                                        )}
                                                                    </>
                                                                )}
                                                            />
                                                        </Col>

                                                        {/* GET REPORT BUTTON */}
                                                        <Col md="2">
                                                            <FormGroup className="d-flex gap-3">
                                                                <Btn
                                                                    color="primary"
                                                                    className="w-100"
                                                                    onClick={handleSubmit(onSubmitReport)}
                                                                >
                                                                    Get
                                                                </Btn>
                                                                <Btn
                                                                    color="secondary"
                                                                    className="w-100"
                                                                    onClick={() => {
                                                                        setDataIntegrityReportData([]);
                                                                        reset();
                                                                    }}
                                                                >
                                                                    Reset
                                                                </Btn>
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>
                                                </Col>

                                                {/* REPORT DISPLAY */}
                                                <Col md="12">
                                                    {filteredIntegrityData.length > 0 ? (
                                                        <EnergyDataIntegrityReport data={filteredIntegrityData} />
                                                    ) : (
                                                        <div className="text-center text-muted">
                                                            No energy data Integrity report available
                                                        </div>
                                                    )}
                                                </Col>
                                            </Row>
                                        </TabPane>
                                        <TabPane tabId='add'>
                                            {/* <Table title='' tableColumns={meterTableColumns} data={meters} /> */}
                                        </TabPane>
                                    </TabContent>
                                </CardBody>
                            </CardBody>
                        </Card>
                    </Col>
                    {(basicTab === 'chart' || basicTab === 'energy-report') &&
                        <Col md="3">
                            <Card>
                                <CardBody>
                                    <Form
                                        className="theme-form"
                                        onSubmit={basicTab === 'chart' ? getChartData : getTableData}
                                    >
                                        <H4 className='text-center'>Filter</H4>
                                        <Row className='text-start'>
                                            {basicTab === 'chart' && (
                                                <Col md="12">
                                                    <Row className="text-center align-center pl-2">
                                                        <Col
                                                            className={`border ${activeChart === 'bar' ? 'bg-primary' : ''} cursor-pointer`}
                                                            onClick={() => setActiveChart('bar')}
                                                        >
                                                            <ChartColumnIncreasing />
                                                        </Col>
                                                        <Col
                                                            className={`border ${activeChart === 'line' ? 'bg-primary' : ''} cursor-pointer`}
                                                            onClick={() => setActiveChart('line')}
                                                        >
                                                            <ChartLine />
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            )}

                                            <Col className='mt-2' md='12'>
                                                <FormGroup>
                                                    <SelectDropdown title={'Properties'} placeholder='Property' options={propertyOptions} value={selectedProperty} onOptionSelected={(val) => handlePropertyChange(val)} />
                                                    <div className="alert alert-danger" id="company_mess_div" hidden>
                                                        <p id="company_message"></p>
                                                    </div>
                                                </FormGroup>
                                            </Col>
                                            <Col className='mt-2' md='12'>
                                                <FormGroup>
                                                    <SelectDropdown title={'Utilities'} placeholder='Utility' options={utilityOptions} value={selectedUtility} onOptionSelected={(val) => { handleUtilityChange(val); }} onOptionTextSelected={(val) => { setUtility(String(val)) }} />
                                                    <div className="alert alert-danger" id="company_mess_div" hidden>
                                                        <p id="company_message"></p>
                                                    </div>
                                                </FormGroup>
                                            </Col>
                                            {activeMode === 'Single' &&
                                                <Col className='mt-2' md='12'>
                                                    <FormGroup>
                                                        <SelectDropdown title={'Meters'} placeholder='Meter' options={meterOptions} value={selectedMeter} onOptionSelected={(val) => setSelectedMeter(val as string)} />
                                                        <div className="alert alert-danger" id="company_mess_div" hidden>
                                                            <p id="company_message"></p>
                                                        </div>
                                                    </FormGroup>
                                                </Col>
                                            }
                                            <Col md='12'>
                                                <FormGroup>
                                                    <Label>Mode Selection</Label>
                                                    <Row className='text-center'>
                                                        <Col md="6" className={`border py-1 ${timeMode === "Multiyear" ? "bg-primary" : ""} cursor-pointer`} onClick={() => setTimeMode("Multiyear")}>Multiyear</Col>
                                                        <Col md="6" className={`border py-1 ${timeMode === "Year" ? "bg-primary" : ""} cursor-pointer`} onClick={() => setTimeMode("Year")}>Year</Col>
                                                    </Row>
                                                    <Row className='text-center'>
                                                        <Col md="6" className={`border py-1 ${timeMode === "Monthly" ? "bg-primary" : ""} cursor-pointer`} onClick={() => setTimeMode("Monthly")}>Monthly</Col>
                                                        <Col md="6" className={`border py-1 ${timeMode === "Weekly" ? "bg-primary" : ""} cursor-pointer`} onClick={() => setTimeMode("Weekly")}>Weekly</Col>
                                                    </Row>
                                                    <Row className='text-center'>
                                                        <Col md="6" className={`border py-1 ${timeMode === "Daily" ? "bg-primary" : ""} cursor-pointer`} onClick={() => setTimeMode("Daily")}>Daily</Col>
                                                        <Col md="6" className={`border py-1 ${timeMode === "From/To" ? "bg-primary" : ""} cursor-pointer`} onClick={() => setTimeMode("From/To")}>From/To</Col>
                                                    </Row>
                                                    <div className="alert alert-danger" id="mode_mess_div" hidden>
                                                        <p id="mode_message"></p>
                                                    </div>
                                                </FormGroup>
                                            </Col>
                                            {(timeMode === 'Year' || timeMode === 'Monthly' || timeMode === 'Daily') &&
                                                <Col md='12'>
                                                    <FormGroup>
                                                        <Row className='justify-content-center text-center'>
                                                            {availableYears.length > 0 ? availableYears.map((year) => (
                                                                <Col
                                                                    md="3"
                                                                    key={year}
                                                                    className={`border py-1 m-1 ${selectedYear === year ? "bg-primary" : ""} cursor-pointer`}
                                                                    style={{ fontSize: '0.6rem' }}
                                                                    onClick={() => { setSelectedYear(year); setSelectedMonth(undefined); setSelectedDay(undefined); }}
                                                                >
                                                                    {year}
                                                                </Col>
                                                            )) : (
                                                                <Col md="12" className='text-muted'>No years available</Col>
                                                            )}
                                                        </Row>
                                                    </FormGroup>
                                                </Col>
                                            }
                                            {(timeMode === 'Monthly' || timeMode === 'Daily') &&
                                                <Col md='12'>
                                                    <FormGroup>
                                                        <Row className='justify-content-center text-center'>
                                                            {months}
                                                        </Row>
                                                    </FormGroup>
                                                </Col>
                                            }
                                            {timeMode === 'Daily' &&
                                                <Col md='12'>
                                                    <FormGroup>
                                                        {days}
                                                    </FormGroup>
                                                </Col>
                                            }
                                            {timeMode === 'From/To' &&
                                                <>
                                                    <Col md="12">
                                                        <FormGroup>
                                                            <Label>From</Label>
                                                            <Input
                                                                type="date"
                                                                value={startDate || ''}
                                                                onChange={e => {
                                                                    setStartDate(e.target.value);
                                                                }}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md="12">
                                                        <FormGroup>
                                                            <Label>To</Label>
                                                            <Input
                                                                type="date"
                                                                value={endDate || ''}
                                                                onChange={e => {
                                                                    setEndDate(e.target.value);
                                                                }}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </>
                                            }
                                            {basicTab === 'chart' && (timeMode === 'Year' || timeMode === 'Monthly') && (
                                                <Col md="12">
                                                    <FormGroup check>
                                                        <Label check>
                                                            <Input
                                                                type="checkbox"
                                                                checked={withPreviousYear}
                                                                onChange={(e) => {
                                                                    const checked = e.target.checked;
                                                                    setWithPreviousYear(checked);
                                                                    if (checked) setWithPreviousMonth(false);
                                                                }}
                                                            />{' '}
                                                            With Previous Year
                                                        </Label>
                                                    </FormGroup>
                                                </Col>
                                            )}
                                            {basicTab === 'chart' && timeMode === 'Monthly' && (
                                                <Col md="12">
                                                    <FormGroup check>
                                                        <Label check>
                                                            <Input
                                                                type="checkbox"
                                                                checked={withPreviousMonth}
                                                                onChange={(e) => {
                                                                    const checked = e.target.checked;
                                                                    setWithPreviousMonth(checked);
                                                                    if (checked) setWithPreviousYear(false);
                                                                }}
                                                            />{' '}
                                                            With Previous Month
                                                        </Label>
                                                    </FormGroup>
                                                </Col>
                                            )}
                                            {basicTab === 'chart' && timeMode === 'Daily' && (
                                                <Col md="12">
                                                    <FormGroup check>
                                                        <Label check>
                                                            <Input
                                                                type="checkbox"
                                                                checked={withPreviousDay}
                                                                onChange={(e) => setWithPreviousDay(e.target.checked)}
                                                            />{' '}
                                                            With Previous Day
                                                        </Label>
                                                    </FormGroup>
                                                </Col>
                                            )}

                                        </Row>
                                        <Row className='justify-content-center'>
                                            <Col md="12">
                                                <Btn className='w-full' color="primary" type="submit">Get Report</Btn>
                                            </Col>
                                        </Row>
                                    </Form>

                                </CardBody>
                            </Card>
                        </Col>
                    }
                </Row>
                <Row>
                </Row>
            </Container >
        </>
    );
}